import { FC } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardContent,
  Divider,
  Chip,
  Box,
} from '@mui/material';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useAcademicsLessonPlansState } from '../../contexts/academicsLessonPlans.context';
import { ELessonPlanStatus } from '../../../../../apis/academics/academicsLessonPlans.interfaces';

const AcademicsLessonPlanDetailsView: FC = () => {
  const { translateText } = useTranslation();
  const { selectedLessonPlan } = useAcademicsLessonPlansState();

  if (!selectedLessonPlan) {
    return null;
  }

  const statusColors: Record<
    ELessonPlanStatus,
    'default' | 'success' | 'warning' | 'error' | 'info'
  > = {
    [ELessonPlanStatus.DRAFT]: 'default',
    [ELessonPlanStatus.IN_REVIEW]: 'info',
    [ELessonPlanStatus.APPROVED]: 'success',
    [ELessonPlanStatus.ACTIVE]: 'success',
    [ELessonPlanStatus.ARCHIVED]: 'default',
  };

  return (
    <Card variant="outlined" data-testid="lesson-plan-details-card">
      <CardContent sx={{ p: { xs: 1, sm: 2 }, '&:last-child': { pb: { xs: 1, sm: 2 } } }}>
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{ mb: { xs: 1, sm: 1.5 }, fontSize: { xs: '1rem', sm: '1.25rem' } }}
          data-testid="lesson-plan-details-title"
        >
          {translateText('academics.lessonPlans.details.title')}
        </Typography>
        <Divider sx={{ mb: { xs: 1, sm: 1.5 } }} />
        <Grid container spacing={{ xs: 1, sm: 2 }}>
          <Grid item xs={12} sm={6} md={3} data-testid="detail-title">
            <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.75rem' } }}>
              {translateText('academics.lessonPlans.columns.title')}
            </Typography>
            <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              {selectedLessonPlan.title ||
                translateText('academics.lessonPlans.nA')}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3} data-testid="detail-subject">
            <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.75rem' } }}>
              {translateText('academics.lessonPlans.columns.subject')}
            </Typography>
            <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              {selectedLessonPlan.subject ||
                translateText('academics.lessonPlans.nA')}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3} data-testid="detail-grade-level">
            <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.75rem' } }}>
              {translateText('academics.lessonPlans.columns.gradeLevel')}
            </Typography>
            <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              {selectedLessonPlan.gradeLevel ||
                translateText('academics.lessonPlans.nA')}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3} data-testid="detail-academic-year">
            <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.75rem' } }}>
              {translateText('academics.lessonPlans.columns.academicYear')}
            </Typography>
            <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              {selectedLessonPlan.academicYear ||
                translateText('academics.lessonPlans.nA')}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3} data-testid="detail-board">
            <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.75rem' } }}>
              {translateText('academics.lessonPlans.columns.board')}
            </Typography>
            <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              {selectedLessonPlan.board ||
                translateText('academics.lessonPlans.nA')}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3} data-testid="detail-status">
            <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.75rem' } }}>
              {translateText('academics.lessonPlans.columns.status')}
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <Chip
                label={
                  selectedLessonPlan.status ||
                  translateText('academics.lessonPlans.nA')
                }
                color={statusColors[selectedLessonPlan.status] || 'default'}
                size="small"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.75rem' } }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3} data-testid="detail-duration">
            <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.75rem' } }}>
              {translateText('academics.lessonPlans.columns.duration')}
            </Typography>
            <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              {selectedLessonPlan.totalDuration
                ? `${selectedLessonPlan.totalDuration} ${translateText('academics.lessonPlans.days')}`
                : translateText('academics.lessonPlans.nA')}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3} data-testid="detail-term">
            <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.75rem' } }}>
              {translateText('academics.lessonPlans.columns.term')}
            </Typography>
            <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              {selectedLessonPlan.term ||
                translateText('academics.lessonPlans.nA')}
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            md={3}
            data-testid="detail-curriculum-framework"
          >
            <Typography variant="caption" color="textSecondary">
              {translateText(
                'academics.lessonPlans.columns.curriculumFramework'
              )}
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {selectedLessonPlan.curriculumFramework ||
                translateText('academics.lessonPlans.nA')}
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            md={3}
            data-testid="detail-syllabus-reference"
          >
            <Typography variant="caption" color="textSecondary">
              {translateText('academics.lessonPlans.columns.syllabusReference')}
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {selectedLessonPlan.syllabusReference ||
                translateText('academics.lessonPlans.nA')}
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            md={3}
            data-testid="detail-textbook-reference"
          >
            <Typography variant="caption" color="textSecondary">
              {translateText('academics.lessonPlans.columns.textbookReference')}
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {selectedLessonPlan.textbookReference ||
                translateText('academics.lessonPlans.nA')}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3} data-testid="detail-units">
            <Typography variant="caption" color="textSecondary">
              {translateText('academics.lessonPlans.columns.units')}
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {selectedLessonPlan.units?.length || 0}
            </Typography>
          </Grid>
          {selectedLessonPlan.description && (
            <Grid item xs={12} data-testid="detail-description">
              <Typography variant="caption" color="textSecondary">
                {translateText('common.description')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedLessonPlan.description}
              </Typography>
            </Grid>
          )}
          {selectedLessonPlan.tags && selectedLessonPlan.tags.length > 0 && (
            <Grid item xs={12} data-testid="detail-tags">
              <Typography variant="caption" color="textSecondary">
                {translateText('academics.lessonPlans.columns.tags')}
              </Typography>
              <Box
                sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
              >
                {selectedLessonPlan.tags.map((tag, index) => (
                  <Chip
                    key={index}
                    label={tag}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Grid>
          )}
          {selectedLessonPlan.actions?.created?.timestamp && (
            <Grid item xs={12} sm={6} md={3} data-testid="detail-created-at">
              <Typography variant="caption" color="textSecondary">
                {translateText('common.created')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {new Date(
                  selectedLessonPlan.actions.created.timestamp
                ).toLocaleDateString()}
              </Typography>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default AcademicsLessonPlanDetailsView;
